import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface ElectionDetail {
  election: {
    id: string;
    title: string;
    description: string | null;
    status: string;
    proposed_action_text: string;
    opens_at: number;
    closes_at: number;
    quorum_pct: number;
    passing_threshold: string;
    custom_pct_value: number | null;
    notice_sent_at: number | null;
    governing_docs_reference: string | null;
  };
  questions: Array<{ id: string; position: number; question_text: string; question_type: string }>;
  options: Array<{ id: string; question_id: string; position: number; option_text: string }>;
}

interface Tally {
  status: string;
  quorumPct: number;
  totalEligibleLotWeight: number;
  totalParticipatingLotWeight: number;
  quorumMet: boolean;
  questions: Array<{
    questionId: string;
    questionText: string;
    questionType: string;
    perOption: Array<{ optionId: string; optionText: string; weight: number; lotCount: number }>;
    winnerOptionId?: string;
    passingThresholdMet?: boolean;
  }>;
}

export function ElectionDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const detail = useQuery<ElectionDetail>({ queryKey: ['election', id], queryFn: () => api.get(`/elections/${id}`) });
  const tally = useQuery<Tally>({
    queryKey: ['election-results', id],
    queryFn: () => api.get(`/elections/${id}/results`),
    enabled: detail.data?.election.status === 'open' || detail.data?.election.status === 'closed',
    refetchInterval: detail.data?.election.status === 'open' ? 10_000 : false,
  });

  const launch = useMutation({
    mutationFn: () => api.post<{ ok: boolean; tokensIssued: number }>(`/elections/${id}/launch`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['election', id] }),
  });
  const close = useMutation({
    mutationFn: () => api.post(`/elections/${id}/close`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['election', id] }); qc.invalidateQueries({ queryKey: ['election-results', id] }); },
  });

  if (detail.isLoading) return <p className="text-slate-500">Loading...</p>;
  if (detail.error) return <p className="text-red-600">Error loading election: {detail.error.message}</p>;
  if (!detail.data) return null;
  const e = detail.data.election;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold">{e.title}</h1>
        <span className={`px-2 py-0.5 rounded text-xs ${e.status === 'open' ? 'bg-green-100 text-green-700' : e.status === 'closed' ? 'bg-slate-200 text-slate-700' : e.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{e.status}</span>
      </div>
      {e.description && <p className="text-slate-600">{e.description}</p>}

      <div className="mt-4 p-4 border-l-4 border-slate-300 bg-slate-50 rounded">
        <div className="text-xs uppercase tracking-wide text-slate-500">Proposed action</div>
        <p className="mt-1 whitespace-pre-wrap text-sm">{e.proposed_action_text}</p>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Stat label="Opens" value={new Date(e.opens_at).toLocaleString()} />
        <Stat label="Closes" value={new Date(e.closes_at).toLocaleString()} />
        <Stat label="Quorum" value={`${e.quorum_pct}%`} />
        <Stat label="Threshold" value={e.passing_threshold + (e.custom_pct_value ? ` (${e.custom_pct_value}%)` : '')} />
      </div>

      {e.governing_docs_reference && (
        <p className="mt-2 text-xs text-amber-700">Override authority: {e.governing_docs_reference}</p>
      )}

      <div className="mt-6 flex gap-2">
        {e.status === 'draft' && (
          <button onClick={() => launch.mutate()} disabled={launch.isPending}
            className="px-4 py-2 bg-brand text-white rounded font-medium disabled:opacity-50">
            {launch.isPending ? 'Launching...' : 'Launch election (issue ballots + send notices)'}
          </button>
        )}
        {e.status === 'open' && (
          <button onClick={() => { if (confirm('Close the election now? Voters with unused tokens will not be able to vote.')) close.mutate(); }} disabled={close.isPending}
            className="px-4 py-2 bg-slate-700 text-white rounded font-medium disabled:opacity-50">
            {close.isPending ? 'Closing...' : 'Close early'}
          </button>
        )}
        {(e.status === 'open' || e.status === 'closed') && (
          <a href={`/api/v1/reports/elections/${id}/audit-pdf`} target="_blank" rel="noreferrer"
            className="px-4 py-2 border border-slate-300 rounded font-medium hover:bg-slate-100">
            Download audit PDF
          </a>
        )}
      </div>
      {launch.error && <div className="mt-2 text-sm text-red-600">{launch.error.message}</div>}
      {launch.data && <div className="mt-2 text-sm text-green-700">Launched. {launch.data.tokensIssued} ballot tokens issued and notice emails sent.</div>}

      <div className="mt-8">
        <h2 className="text-lg font-bold mb-3">Questions ({detail.data.questions.length})</h2>
        {detail.data.questions.map((q) => {
          const opts = detail.data!.options.filter((o) => o.question_id === q.id);
          const qTally = tally.data?.questions.find((t) => t.questionId === q.id);
          return (
            <div key={q.id} className="bg-white border border-slate-200 rounded p-4 mb-3">
              <div className="font-medium">{q.question_text}</div>
              <div className="text-xs text-slate-500 mb-2">Type: {q.question_type}</div>
              {qTally ? (
                <ul className="space-y-1 text-sm">
                  {opts.map((o) => {
                    const t = qTally.perOption.find((p) => p.optionId === o.id);
                    const w = t?.weight ?? 0;
                    const total = qTally.perOption.reduce((s, p) => s + p.weight, 0) || 1;
                    const pct = (w / total) * 100;
                    const winner = qTally.winnerOptionId === o.id;
                    return (
                      <li key={o.id} className="flex items-center gap-2">
                        <span className="w-32 truncate">{o.option_text}</span>
                        <div className="flex-1 bg-slate-100 rounded h-3 overflow-hidden">
                          <div className={`h-full ${winner ? 'bg-brand' : 'bg-slate-400'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-20 text-right text-xs text-slate-600">{w.toFixed(2)} ({pct.toFixed(1)}%)</span>
                        {winner && <span className="text-brand font-bold">★</span>}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <ul className="text-sm text-slate-600 list-disc pl-5">
                  {opts.map((o) => <li key={o.id}>{o.option_text}</li>)}
                </ul>
              )}
              {qTally && <div className="mt-2 text-xs">{qTally.passingThresholdMet ? <span className="text-green-700">Passing threshold met</span> : <span className="text-slate-500">Threshold not met</span>}</div>}
            </div>
          );
        })}
      </div>

      {tally.data && (
        <div className="mt-6 p-4 border border-slate-200 rounded bg-white">
          <h2 className="text-lg font-bold mb-2">Quorum</h2>
          <p className="text-sm">Eligible weight: <strong>{tally.data.totalEligibleLotWeight.toFixed(2)}</strong> · Voted: <strong>{tally.data.totalParticipatingLotWeight.toFixed(2)}</strong> ({tally.data.totalEligibleLotWeight ? ((tally.data.totalParticipatingLotWeight / tally.data.totalEligibleLotWeight) * 100).toFixed(2) : '0'}%)</p>
          <p className="text-sm mt-1">Required: {tally.data.quorumPct}% · {tally.data.quorumMet ? <span className="text-green-700 font-semibold">Quorum met</span> : <span className="text-red-700 font-semibold">Quorum NOT met</span>}</p>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
